/**
 * Shared Address API functions
 * Addresses are used by contacts, suppliers, customers, etc.
 */

import { apiClient, type PaginatedResponse } from '../../../api/client'
import { buildPaginationParams, type PaginationOptions } from '../../../api/pagination'
import LogTool from '../../../logger/logTools'
import { getPKfromSelf } from '../../../utils/functions'
import { type Address } from '../../../utils/types'
import { APIAddressFromJSON } from '../../../generated-types/models/APIAddress'

const log = new LogTool({ context: 'AddressAPI', enable: true, logLevel: 'warn' })

/**
 * Create Address from API response
 */
export function createAddress(addressResponse: any): Address {
  const apiAddress = APIAddressFromJSON(addressResponse)
  const extras =
    typeof addressResponse === 'object' && addressResponse !== null
      ? addressResponse
      : {}
  return {
    ...apiAddress,
    ...extras,
    key: getPKfromSelf(apiAddress.self),
  }
}

/**
 * Convert form input to API JSON
 */
export function formInputToAPIAddressJSON(addressInput: any): any {
  return {
    ...(addressInput.address1 && { address_1: addressInput.address1 }),
    ...(addressInput.address2 && { address_2: addressInput.address2 }),
    ...(addressInput.address3 && { address_3: addressInput.address3 }),
    ...(addressInput.zipCode && { zip_code: addressInput.zipCode }),
    ...(addressInput.city && { city: addressInput.city }),
    ...(addressInput.country && { country: addressInput.country }),
    ...(addressInput.type && { type: addressInput.type }),
    ...(addressInput.info && { info: addressInput.info }),
  }
}

/**
 * Fetch paginated addresses from API
 */
export async function fetchAddresses(
  options: PaginationOptions = {}
): Promise<PaginatedResponse<Address>> {
  log.info('Fetching addresses with options:', options)

  const params = buildPaginationParams(options)
  const response = await apiClient.get<PaginatedResponse<any>>(`v1/contacts/addresses/?${params.join('&')}`)

  const addresses = response.results.map((a: any) => createAddress(a))

  return {
    ...response,
    results: addresses,
  }
}

/**
 * Fetch a single address by ID or URL
 */
export async function fetchAddress(
  idOrUrl: string | number
): Promise<Address> {
  log.info('Fetching address:', idOrUrl)

  const url = typeof idOrUrl === 'number' || !idOrUrl.includes('/')
    ? `v1/contacts/addresses/${idOrUrl}/`
    : idOrUrl

  const response = await apiClient.get<any>(url)
  return createAddress(response)
}

/**
 * Create a new address
 */
export async function createAddressApi(addressData: any): Promise<Address> {
  log.info('Creating address:', addressData)

  const json = formInputToAPIAddressJSON(addressData)
  const response = await apiClient.post<any>('v1/contacts/addresses/', json)

  return createAddress(response)
}

/**
 * Update an existing address
 */
export async function updateAddressApi(
  idOrSelf: string | number,
  addressData: any
): Promise<Address> {
  log.info('Updating address:', idOrSelf, addressData)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/contacts/addresses/${idOrSelf}/`
    : idOrSelf

  const json = formInputToAPIAddressJSON(addressData)
  const response = await apiClient.put<any>(url, json)

  return createAddress(response)
}

/**
 * Patch an existing address (partial update)
 */
export async function patchAddressApi(
  idOrSelf: string | number,
  addressData: Partial<any>
): Promise<Address> {
  log.info('Patching address:', idOrSelf, addressData)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/contacts/addresses/${idOrSelf}/`
    : idOrSelf

  const json = formInputToAPIAddressJSON(addressData)
  const response = await apiClient.patch<any>(url, json)

  return createAddress(response)
}

/**
 * Delete an address
 */
export async function deleteAddressApi(idOrSelf: string | number): Promise<void> {
  log.info('Deleting address:', idOrSelf)

  const url = typeof idOrSelf === 'number' || !idOrSelf.includes('/')
    ? `v1/contacts/addresses/${idOrSelf}/`
    : idOrSelf

  await apiClient.delete(url)
}

/**
 * Delete multiple addresses
 */
export async function deleteAddressesApi(addresses: Address[]): Promise<void> {
  log.info('Deleting multiple addresses:', addresses.length)

  await Promise.all(
    addresses.map(address => deleteAddressApi(address.self))
  )
}

/**
 * Fetch all addresses (recursively fetches all pages)
 * Note: Use with caution for large datasets
 */
export async function fetchAllAddresses(
  options: Omit<PaginationOptions, 'page'> = {}
): Promise<Address[]> {
  log.info('Fetching all addresses')

  let allAddresses: Address[] = []
  let page = 1
  let hasMore = true

  while (hasMore) {
    const response = await fetchAddresses({ ...options, page })
    allAddresses = [...allAddresses, ...response.results]
    
    hasMore = response.next !== null
    page++
  }

  log.info(`Fetched ${allAddresses.length} addresses total`)
  return allAddresses
}

/**
 * Fetch all addresses as options for select inputs (all pages)
 * Returns options in the format: { value: string, label: string }
 * Label format: "address_1, zip_code, city"
 * Note: Use with caution for large datasets
 */
export async function fetchAddressOptions(): Promise<
  { value: string; label?: string; disabled?: boolean }[]
> {
  log.info('Fetching address options')

  const allAddresses = await fetchAllAddresses()
  
  return allAddresses
    .filter(address => address.self !== undefined)
    .map(address => ({
      value: address.self,
      label: `${address.address1 ?? ''}, ${address.zipCode ?? ''}, ${address.city ?? ''}`,
    }))
}

